import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  Breadcrumb,
  Button,
  Empty,
  message as antdMessage,
  Spin,
  Tooltip,
  Typography,
  Dropdown,
  Input,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  UserOutlined,
  RobotOutlined,
  MoreOutlined,
  EditOutlined,
  MessageOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useSearch } from '../contexts/SearchContext';
import {
  getWidget,
  getWidgetSessions,
  getWidgetSessionMessages,
  searchWidgetSessionMessages,
  regenerateWidgetSessionSummary,
  patchWidgetSessionSummary,
} from '../services/api/widgets';
import { closeShareContinuation, getShareReview } from '../services/api/chatShares';
import { getPipelineTraceVisibility } from '../services/api/platformSettings';
import { getCurrentUserRoles, fetchAssistant } from '../services/api';
import { copyText } from '../utils/clipboard';
import { relativeTime } from '../utils/relativeTime';
import { fullDateTime } from '../utils/dateTimeFormat';
import { mediaUrl } from '../utils/mediaUrl';
import { assistantDisplayImageSrc } from '../utils/assistantVisual';
import AssistantMarkdown from '../components/chat/AssistantMarkdown';
import PipelineTracePanel from '../components/chat/PipelineTracePanel';
import { MessageActions } from '../components/chat/MessageActionsBar';
import ChatMessageAvatar from '../components/chat/ChatMessageAvatar';
import './WidgetSessionsMessenger.css';
import './Chat.css';

const { Text, Title } = Typography;

function shortSid(id) {
  if (!id) return '';
  return id.length > 10 ? `${id.substring(0, 8)}…` : id;
}

function trunc(s, n = 36) {
  if (!s) return '';
  const t = s.trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n - 1)}…`;
}

function withMessageIds(messages, prefix) {
  return (messages || []).map((message, index) => ({
    ...message,
    id: message.id ?? `${prefix}-${index}`,
    content: message.content || '',
  }));
}

export default function WidgetSessionsMessenger() {
  const { widgetId, sessionId: sessionIdParam, shareId } = useParams();
  const shareMode = Boolean(shareId);
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { query, registerSearchHandler } = useSearch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [widget, setWidget] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const selectedSessionId = sessionIdParam || null;
  const [regenLoading, setRegenLoading] = useState(false);
  const [pipelineVis, setPipelineVis] = useState(null);
  const [messageSearchHits, setMessageSearchHits] = useState([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [headerEdit, setHeaderEdit] = useState(false);
  const [headerDraft, setHeaderDraft] = useState('');
  const [linkedAssistant, setLinkedAssistant] = useState(null);
  const [shareMeta, setShareMeta] = useState(null);
  const [shareVersion, setShareVersion] = useState(0);
  const messagesBySession = useRef({});

  const messageRefs = useRef(new Map());
  const wid = useMemo(() => (widgetId ? parseInt(String(widgetId), 10) : NaN), [widgetId]);

  const assistantImg = useMemo(() => {
    if (shareMode) return assistantDisplayImageSrc(shareMeta);
    if (widget?.avatar_url) {
      const u = mediaUrl(widget.avatar_url);
      if (u) return u;
    }
    return assistantDisplayImageSrc(linkedAssistant);
  }, [shareMode, shareMeta, widget?.avatar_url, linkedAssistant]);

  const goSession = useCallback(
    (sid, msgId) => {
      if (shareMode) {
        const path = `/channels/shares/${shareId}/${encodeURIComponent(sid)}`;
        if (msgId) setSearchParams({ m: String(msgId) });
        else setSearchParams({});
        navigate(path);
        return;
      }
      if (!Number.isFinite(wid)) return;
      const path = `/channels/widgets/${wid}/sessions/${encodeURIComponent(sid)}`;
      if (msgId) {
        setSearchParams({ m: String(msgId) });
        navigate(path);
      } else {
        setSearchParams({});
        navigate(path);
      }
    },
    [navigate, wid, setSearchParams, shareMode, shareId]
  );

  useEffect(() => {
    if (!shareMode || !keycloak?.authenticated || !shareId) return undefined;
    let ok = true;
    (async () => {
      try {
        setSessionsLoading(true);
        const review = await getShareReview(keycloak, shareId);
        if (!ok) return;
        const buckets = {};
        const transcript = withMessageIds(review.transcript, 'transcript');
        buckets.transcript = transcript;
        const nextSessions = [{
          id: 'transcript',
          session_id: 'transcript',
          summary: 'Shared transcript',
          message_count: transcript.length,
          last_activity_at: transcript[transcript.length - 1]?.created_at || null,
        }];
        let guestCount = 0;
        let userCount = 0;
        (review.continuations || []).forEach((item) => {
          const sid = String(item.id);
          const msgs = withMessageIds(item.messages, sid);
          buckets[sid] = msgs;
          const guest = Boolean(item.guest);
          const n = guest ? ++guestCount : ++userCount;
          const label = guest
            ? (guestCount > 1 ? `Guest ${n}` : 'Guest')
            : (userCount > 1 ? `Signed-in user ${n}` : 'Signed-in user');
          nextSessions.push({
            id: item.id,
            session_id: sid,
            summary: item.closed ? `${label} · closed` : label,
            message_count: msgs.length,
            last_activity_at: msgs[msgs.length - 1]?.created_at || null,
            closed: Boolean(item.closed),
          });
        });
        messagesBySession.current = buckets;
        setShareMeta({
          name: review.assistant_name || 'Shared chat',
          assistantRef: review.assistant_id,
          avatar_url: review.avatar_url,
          model_icon: review.model_icon,
        });
        setSessions(nextSessions);
      } catch (e) {
        antdMessage.error(e.message || 'Failed to load shared chats');
        if (ok) {
          setShareMeta(null);
          setSessions([]);
        }
      } finally {
        if (ok) setSessionsLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [shareMode, keycloak, shareId, shareVersion]);

  useEffect(() => {
    if (shareMode || !keycloak?.authenticated || !Number.isFinite(wid)) return undefined;
    let ok = true;
    (async () => {
      try {
        setSessionsLoading(true);
        const w = await getWidget(keycloak, wid);
        const s = await getWidgetSessions(keycloak, wid);
        if (ok) {
          setWidget(w);
          setSessions(Array.isArray(s) ? s : []);
        }
      } catch (e) {
        antdMessage.error(e.message || 'Failed to load widget');
        if (ok) {
          setWidget(null);
          setSessions([]);
        }
      } finally {
        if (ok) setSessionsLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [keycloak, wid, shareMode]);

  useEffect(() => {
    if (!keycloak?.authenticated || !widget?.assistant_id) {
      setLinkedAssistant(null);
      return;
    }
    let cancelled = false;
    fetchAssistant(keycloak, widget.assistant_id)
      .then((a) => {
        if (!cancelled) setLinkedAssistant(a);
      })
      .catch(() => {
        if (!cancelled) setLinkedAssistant(null);
      });
    return () => {
      cancelled = true;
    };
  }, [keycloak, widget?.assistant_id]);

  useEffect(() => {
    if (!keycloak) return;
    getPipelineTraceVisibility(keycloak)
      .then(setPipelineVis)
      .catch(() => setPipelineVis(null));
    getCurrentUserRoles(keycloak)
      .then((r) => setIsPlatformAdmin(Boolean(r?.is_admin)))
      .catch(() => setIsPlatformAdmin(false));
  }, [keycloak]);

  useEffect(() => {
    if (shareMode) {
      if (!selectedSessionId || sessionsLoading) {
        if (!selectedSessionId) setMessages([]);
        return undefined;
      }
      setMessages(messagesBySession.current[selectedSessionId] || []);
      return undefined;
    }
    if (!keycloak?.authenticated || !Number.isFinite(wid) || !selectedSessionId) {
      setMessages([]);
      return undefined;
    }
    let ok = true;
    (async () => {
      setMessagesLoading(true);
      try {
        const m = await getWidgetSessionMessages(keycloak, wid, selectedSessionId);
        if (ok) setMessages(m || []);
      } catch (e) {
        antdMessage.error(e.message || 'Failed to load messages');
        if (ok) setMessages([]);
      } finally {
        if (ok) setMessagesLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [keycloak, wid, selectedSessionId, shareMode, sessionsLoading, sessions]);

  const scrollTarget = searchParams.get('m');
  useEffect(() => {
    if (!scrollTarget || !messages.length) return;
    const t = setTimeout(() => {
      const el = messageRefs.current.get(Number(scrollTarget)) || messageRefs.current.get(String(scrollTarget));
      el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }, 200);
    return () => clearTimeout(t);
  }, [scrollTarget, messages, selectedSessionId]);

  useEffect(() => {
    if (!keycloak || !Number.isFinite(wid)) {
      return undefined;
    }
    const t = setTimeout(() => {
      const q = (query || '').trim();
      if (q.length < 1) {
        setMessageSearchHits([]);
        return;
      }
      searchWidgetSessionMessages(keycloak, wid, q, 24)
        .then((rows) => setMessageSearchHits(Array.isArray(rows) ? rows : []))
        .catch(() => setMessageSearchHits([]));
    }, 280);
    return () => clearTimeout(t);
  }, [query, keycloak, wid]);

  useEffect(
    () =>
      registerSearchHandler('Widget chats', (qRaw) => {
        const q = (qRaw || '').trim().toLowerCase();
        if (!q) return [];
        const fromSessions = (sessions || [])
          .filter(
            (s) =>
              (s.summary || '').toLowerCase().includes(q) || (s.session_id || '').toLowerCase().includes(q)
          )
          .slice(0, 8)
          .map((s) => ({
            id: `sess-${s.id}`,
            title: s.summary || shortSid(s.session_id) || 'Session',
            subtitle: 'Session in list',
            type: 'default',
            action: () => goSession(s.session_id, null),
          }));
        const fromApi = (messageSearchHits || []).map((h) => ({
          id: `msg-${h.message_id}`,
          title: h.content_snippet || 'Message',
          subtitle: `Message in ${shortSid(h.session_id)}`,
          type: 'file',
          action: () => goSession(h.session_id, h.message_id),
        }));
        return [...fromApi, ...fromSessions].slice(0, 20);
      }),
    [registerSearchHandler, sessions, messageSearchHits, goSession]
  );

  const exportTranscript = useCallback(() => {
    const title = shareMode ? (shareMeta?.name || 'Shared chat') : widget?.name;
    if (!title || !selectedSessionId || !messages.length) {
      antdMessage.warning('Nothing to export');
      return;
    }
    const lines = messages.map(
      (m) =>
        `[${m.role}] ${m.created_at ? new Date(m.created_at).toISOString() : ''}\n${m.content || ''}\n`
    );
    const body = `${shareMode ? 'Shared chat' : 'Widget'}: ${title}\nSession: ${selectedSessionId}\n\n${lines.join('\n')}`;
    const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${shareMode ? 'share' : 'widget'}-${title.replace(/[/\\?%*:|"<>]/g, '-')}-session-${shortSid(
      selectedSessionId
    ).replace('…', '')}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [widget, selectedSessionId, messages, shareMode, shareMeta]);

  const shareLink = useCallback(() => {
    if (!selectedSessionId) return;
    if (!shareMode && !Number.isFinite(wid)) return;
    const path = shareMode
      ? `/channels/shares/${shareId}/${encodeURIComponent(selectedSessionId)}`
      : `/channels/widgets/${wid}/sessions/${encodeURIComponent(selectedSessionId)}`;
    const u = new URL(`${window.location.origin}${path}`);
    copyText(u.toString());
    antdMessage.success('Link copied', 1.5);
  }, [wid, selectedSessionId, shareMode, shareId]);

  const onRegenerateSummary = useCallback(async () => {
    if (!keycloak || !Number.isFinite(wid) || !selectedSessionId) return;
    setRegenLoading(true);
    try {
      const r = await regenerateWidgetSessionSummary(keycloak, wid, selectedSessionId);
      setSessions((prev) =>
        prev.map((s) => (s.session_id === selectedSessionId ? { ...s, summary: r.summary } : s))
      );
      antdMessage.success('Title updated', 1.5);
    } catch (e) {
      antdMessage.error(e.message || 'Regenerate failed');
    } finally {
      setRegenLoading(false);
    }
  }, [keycloak, wid, selectedSessionId]);

  const saveSessionTitle = useCallback(async () => {
    if (!keycloak || !Number.isFinite(wid) || !selectedSessionId) return;
    const full = sessions.find((s) => s.session_id === selectedSessionId);
    const old = full?.summary || '';
    const rest = old.includes('\n') ? old.split('\n').slice(1).join('\n') : '';
    const newSummary = rest ? `${headerDraft.trim()}\n${rest}` : headerDraft.trim();
    try {
      const r = await patchWidgetSessionSummary(keycloak, wid, selectedSessionId, newSummary);
      setSessions((prev) =>
        prev.map((s) => (s.session_id === selectedSessionId ? { ...s, summary: r.summary } : s))
      );
      setHeaderEdit(false);
      antdMessage.success('Title saved', 1.2);
    } catch (e) {
      antdMessage.error(e.message || 'Failed to save title');
    }
  }, [keycloak, wid, selectedSessionId, headerDraft, sessions]);

  const showTraceForAdmin =
    pipelineVis?.pipeline_trace_enabled && pipelineVis?.pipeline_trace_show_widget_admin;

  const sessionTitleText = useMemo(() => {
    if (!selectedSessionId) return '';
    const s = sessions.find((x) => x.session_id === selectedSessionId);
    const first = (s?.summary || '').split('\n')[0]?.trim();
    return first || shortSid(selectedSessionId);
  }, [sessions, selectedSessionId]);

  const startEditTitle = useCallback(() => {
    const s = sessions.find((x) => x.session_id === selectedSessionId);
    setHeaderDraft((s?.summary || '').split('\n')[0] || sessionTitleText);
    setHeaderEdit(true);
  }, [sessions, selectedSessionId, sessionTitleText]);

  const onCopySessionId = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!selectedSessionId) return;
      copyText(selectedSessionId);
      antdMessage.success('Session id copied', 1.2);
    },
    [selectedSessionId]
  );

  const closeContinuation = useCallback(async () => {
    const current = sessions.find((item) => item.session_id === selectedSessionId);
    if (!keycloak || !shareId || !current || current.session_id === 'transcript' || current.closed) return;
    try {
      await closeShareContinuation(keycloak, shareId, current.id);
      antdMessage.success('Continuation closed', 1.5);
      setShareVersion((version) => version + 1);
    } catch (e) {
      antdMessage.error(e.message || 'Could not close continuation');
    }
  }, [keycloak, shareId, selectedSessionId, sessions]);

  const moreMenu = useMemo(() => {
    if (!selectedSessionId) return { items: [] };
    if (shareMode) {
      const current = sessions.find((item) => item.session_id === selectedSessionId);
      const items = [
        {
          key: 'export',
          icon: <DownloadOutlined />,
          label: 'Export transcript',
          disabled: !messages.length,
          onClick: () => exportTranscript(),
        },
        {
          key: 'link',
          label: (
            <div>
              <div>Copy link</div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2, whiteSpace: 'normal' }}>
                For you and admins only
              </Text>
            </div>
          ),
          onClick: () => shareLink(),
        },
      ];
      if (current && current.session_id !== 'transcript' && !current.closed) {
        items.push({
          key: 'close',
          icon: <CloseOutlined />,
          label: 'Close continuation',
          onClick: () => closeContinuation(),
        });
      }
      return { items };
    }
    const items = [
      {
        key: 'edit-title',
        icon: <EditOutlined />,
        label: 'Edit title',
        onClick: () => startEditTitle(),
      },
      {
        key: 'export',
        icon: <DownloadOutlined />,
        label: 'Export transcript',
        disabled: !messages.length,
        onClick: () => exportTranscript(),
      },
      {
        key: 'link',
        label: (
          <div>
            <div>Copy link</div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2, whiteSpace: 'normal' }}>
              For you and admins only
            </Text>
          </div>
        ),
        onClick: () => shareLink(),
      },
    ];
    if (isPlatformAdmin) {
      items.push({
        key: 'regen',
        icon: <ReloadOutlined />,
        label: 'Regenerate title',
        disabled: regenLoading,
        onClick: () => onRegenerateSummary(),
      });
    }
    return { items };
  }, [
    selectedSessionId,
    messages.length,
    exportTranscript,
    shareLink,
    isPlatformAdmin,
    regenLoading,
    onRegenerateSummary,
    startEditTitle,
    shareMode,
    sessions,
    closeContinuation,
  ]);

  const closeSessionView = useCallback(() => {
    setHeaderEdit(false);
    setSearchParams({});
    if (shareMode) {
      navigate(`/channels/shares/${shareId}`);
      return;
    }
    if (!Number.isFinite(wid)) return;
    navigate(`/channels/widgets/${wid}/sessions`);
  }, [wid, navigate, setSearchParams, shareMode, shareId]);

  if (!shareMode && !Number.isFinite(wid)) {
    return (
      <div className="wsm-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <Text>Invalid widget</Text>
        <Button onClick={() => navigate('/channels/widgets')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="wsm-root">
      <aside className="wsm-sidebar">
        <div className="wsm-sidebar-top wsm-pane-header">
          <Breadcrumb
            className="wsm-breadcrumb"
            items={shareMode ? [
              {
                title: (
                  <Link to={shareMeta?.assistantRef ? `/assistants/${shareMeta.assistantRef}/channels` : '/assistants'}>
                    Shared chats
                  </Link>
                ),
              },
              {
                title: (
                  <Link to={`/channels/shares/${shareId}`} style={{ color: 'inherit' }}>
                    {shareMeta?.name || (sessionsLoading ? '…' : 'Shared chat')}
                  </Link>
                ),
              },
              selectedSessionId
                ? { title: trunc(sessionTitleText, 40) || 'Conversation' }
                : { title: 'Sessions' },
            ] : [
              {
                title: <Link to="/channels/widgets">Widgets</Link>,
              },
              {
                title: (
                  <Link to={`/channels/widgets/${wid}/sessions`} style={{ color: 'inherit' }}>
                    {widget?.name || (sessionsLoading ? '…' : 'Widget')}
                  </Link>
                ),
              },
              selectedSessionId
                ? { title: trunc(sessionTitleText, 40) || 'Conversation' }
                : { title: 'Sessions' },
            ]}
          />
        </div>
        <div className="wsm-sidebar-list">
          {sessionsLoading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : sessions.length === 0 ? (
            <Empty style={{ marginTop: 24 }} description="No sessions yet" />
          ) : (
            sessions.map((s) => {
              const isSel = s.session_id === selectedSessionId;
              const n = s.message_count ?? 0;
              return (
                <div
                  key={s.id}
                  className={`wsm-session-item ${isSel ? 'wsm-active' : ''}`}
                  onClick={() => goSession(s.session_id, null)}
                >
                  <div className="wsm-session-title">
                    {s.summary?.trim() || shortSid(s.session_id) || 'Session'}
                  </div>
                  <div className="wsm-session-meta">
                    <span>{relativeTime(s.last_activity_at)}</span>
                    <Tooltip title="Messages in this session">
                      <span
                        className="wsm-msg-count"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <MessageOutlined style={{ fontSize: 12, opacity: 0.7 }} />
                        <span style={{ fontSize: 12 }}>{n}</span>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>
      <main className="wsm-main">
        <div
          className={`wsm-main-header wsm-pane-header${selectedSessionId ? ' wsm-main-header--session' : ''}`}
        >
          <div className="wsm-header-main" style={{ minWidth: 0, flex: 1 }}>
            {selectedSessionId ? (
              <div className="wsm-header-session-block">
                <div className="wsm-header-title-row">
                  {headerEdit ? (
                    <Input
                      size="small"
                      value={headerDraft}
                      onChange={(e) => setHeaderDraft(e.target.value)}
                      onBlur={() => {
                        saveSessionTitle();
                      }}
                      onPressEnter={() => {
                        saveSessionTitle();
                      }}
                      className="wsm-header-title-input"
                      autoFocus
                    />
                  ) : (
                    <Text strong className="wsm-header-title-text" ellipsis>
                      {sessionTitleText}
                    </Text>
                  )}
                </div>
                <Tooltip title="Click to copy session id">
                  <Text type="secondary" className="wsm-session-id" onClick={onCopySessionId}>
                    {selectedSessionId}
                  </Text>
                </Tooltip>
              </div>
            ) : (
              <Title level={5} style={{ margin: 0, fontSize: 16 }}>
                Sessions
              </Title>
            )}
          </div>
          {selectedSessionId && (
            <div className="wsm-header-actions" style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0 }}>
              <Dropdown menu={moreMenu} trigger={['click']} placement="bottomRight">
                <Button type="text" icon={<MoreOutlined style={{ color: '#9ca3af', fontSize: 18 }} />} onClick={(e) => e.stopPropagation()} />
              </Dropdown>
              <Tooltip title="Close session">
                <Button
                  type="text"
                  icon={<CloseOutlined style={{ color: '#9ca3af', fontSize: 16 }} />}
                  onClick={closeSessionView}
                  aria-label="Close session"
                />
              </Tooltip>
            </div>
          )}
        </div>
        <div
          className={`wsm-main-scroll${!selectedSessionId ? ' wsm-main-scroll--empty' : ''}`}
        >
          {!selectedSessionId ? (
            <div className="wsm-empty-center">
              <Empty description="Choose a session from the list" />
            </div>
          ) : messagesLoading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : messages.length === 0 ? (
            <Empty description="No messages" />
          ) : (
            <div style={{ maxWidth: 820, margin: '0 auto', padding: '0 0 8px' }}>
              {messages.map((msg) => {
                const isUser = msg.role === 'user';
                const isAssistant = msg.role === 'assistant';
                return (
                  <div
                    key={msg.id}
                    ref={(el) => {
                      if (el) messageRefs.current.set(msg.id, el);
                      else messageRefs.current.delete(msg.id);
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: isUser ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                      gap: 10,
                      marginBottom: 20,
                    }}
                  >
                    <div style={{ paddingTop: 2 }}>
                      {isUser ? (
                        <ChatMessageAvatar icon={<UserOutlined />} bg="#3b82f6" size={30} />
                      ) : (
                        <ChatMessageAvatar
                          src={assistantImg}
                          icon={<RobotOutlined />}
                          size={30}
                        />
                      )}
                    </div>
                    <div className={`chat-message-column${isAssistant ? ' chat-message-column--assistant' : ''}`}>
                      {showTraceForAdmin && !isUser && msg.pipeline_trace && (
                        <PipelineTracePanel variant="aboveBubble" trace={msg.pipeline_trace} />
                      )}
                      <div
                        style={{
                          padding: '10px 16px',
                          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          fontSize: 15,
                          lineHeight: 1.65,
                          background: isUser ? '#3b82f6' : '#f3f4f6',
                          color: isUser ? '#fff' : '#1a1d29',
                          boxShadow: '0 1px 2px rgba(0,0,0,.04)',
                          wordBreak: 'break-word',
                        }}
                      >
                        {isAssistant ? (
                          <div className="chat-stream-markdown-wrap">
                            <AssistantMarkdown content={msg.content} />
                          </div>
                        ) : (
                          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                        )}
                      </div>
                      <div
                        className={isUser ? 'chat-time-tooltip-row chat-time-tooltip-row--user' : 'chat-time-tooltip-row'}
                      >
                        <Tooltip title={fullDateTime(msg.created_at)}>
                          <span className="chat-time-tooltip-wrap">{relativeTime(msg.created_at)}</span>
                        </Tooltip>
                      </div>
                      {isAssistant && (
                        <MessageActions
                          msg={msg}
                          isLast={false}
                          showAlways={!!msg.feedback}
                          feedbackReadOnly
                          onCopy={() => {
                            copyText(msg.content || '');
                            antdMessage.success('Copied', 1.5);
                          }}
                        />
                      )}
                      {isUser && (
                        <MessageActions
                          msg={msg}
                          isLast={false}
                          showAlways={false}
                          onCopy={() => {
                            copyText(msg.content || '');
                            antdMessage.success('Copied', 1.5);
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
